import React, { useState, useEffect } from "react";
import { Spin, message, Card, Table, Select, Button, Tooltip, Modal, Input } from "antd";
import { DownloadOutlined, LockOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import MailingListFilter from '../components/MailingListFilter';
import categoryOptions from '../constants/categoryOptions';
import { exportToExcel, getFormattedDate } from "../../../utils/exportUtils";
import { getMailingLists, getMailFilesTimelineMonthlyReport, getMailFilesTimelineMonthlyDetails } from "../../../core/api/database";

const MFvsTimelineMonthsTab = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [totalUniqueCustomers, setTotalUniqueCustomers] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(['all']);
  const [selectedListCount, setSelectedListCount] = useState('all');
  const [mailingListOptions, setMailingListOptions] = useState([]);
  const [selectedMailingLists, setSelectedMailingLists] = useState(['all']);
  const [loadingMailingLists, setLoadingMailingLists] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState('');
  const [currentMonthYear, setCurrentMonthYear] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);
  
  // Get selectedClients from Redux store
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    fetchMailingLists();
    // Only fetch report data on initial load
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Automatically fetch data when selectedClients changes
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  const fetchMailingLists = async () => {
    setLoadingMailingLists(true);
    try {
      const response = await getMailingLists();
      const lists = response.data;
      
      // Format lists for the Select component
      const formattedOptions = lists.map(list => ({
        label: list,
        value: list
      }));
      
      setMailingListOptions(formattedOptions);
      setLoadingMailingLists(false);
    } catch (error) {
      console.error("Error fetching mailing lists:", error);
      message.error("Failed to fetch mailing lists");
      setLoadingMailingLists(false);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      console.log('Fetching monthly timeline data with params:', {
        selectedClients,
        selectedCategory,
        selectedMailingLists
      });
      
      const response = await getMailFilesTimelineMonthlyReport(
        selectedClients,
        selectedCategory,
        selectedMailingLists,
        selectedListCount
      );
      
      console.log('Monthly Timeline API response:', response);
      
      if (response && response.data) {
        // Add download handler to each row
        const dataWithDownloadHandler = (response.data.data || []).map(item => ({
          ...item,
          key: `${item.year}-${item.month}`,
          onDownload: () => showPasswordModal(item.year, item.month, item.month_year)
        }));
        
        setReportData(dataWithDownloadHandler);
        setTotalUniqueCustomers(response.data.total_unique_customers || 0);
      } else {
        console.error('Invalid response format:', response);
        message.error('Received invalid data format from server');
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching mail files monthly timeline report data:", error);
      message.error(`Failed to fetch monthly timeline data: ${error.message || 'Unknown error'}`);
      setReportData([]);
      setTotalUniqueCustomers(0);
      setLoading(false);
    }
  };
  
  const handleCategoryChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedCategory.includes('all')) {
      setSelectedCategory(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedCategory.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedCategory(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedCategory(value);
    }
  };
  
  const handleListCountChange = (value) => {
    setSelectedListCount(value);
  };
  
  const handleMailingListsChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedMailingLists.includes('all')) {
      setSelectedMailingLists(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedMailingLists.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedMailingLists(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedMailingLists(value);
    }
  };
  
  const handleSelectAllMailingLists = () => {
    setSelectedMailingLists(['all']);
  };
  
  const handleDeselectAllMailingLists = () => {
    if (mailingListOptions.length > 0) {
      const allValues = mailingListOptions.map(option => option.value);
      setSelectedMailingLists(allValues);
    }
  };
  
  const handleApplyFilters = () => {
    fetchReportData();
  };
  
  const handleClearFilters = () => {
    setSelectedCategory(['all']);
    setSelectedMailingLists(['all']);
    setSelectedListCount('all');
    // After clearing filters, apply them immediately
    setTimeout(() => fetchReportData(), 0);
  };

  const handleExportToExcel = () => {
    const fileName = `Mail_Files_Monthly_Timeline_Report_${getFormattedDate()}`;
    const data = reportData.map((row) => ({
      'Month Year': row.month_year,
      'Customer Count': row.customer_count
    }));
    
    const columns = [
      { title: 'Month Year', dataIndex: 'Month Year' },
      { title: 'Customer Count', dataIndex: 'Customer Count' }
    ];
    
    exportToExcel(data, fileName, columns);
  };
  
  // Show password modal before downloading
  const showPasswordModal = (year, month, monthYear) => {
    setCurrentYear(year);
    setCurrentMonth(month);
    setCurrentMonthYear(monthYear);
    setPasswordModalVisible(true);
    setDownloadPassword('');
  };

  // Handle password modal cancel
  const handlePasswordModalCancel = () => {
    setPasswordModalVisible(false);
    setDownloadPassword('');
    setCurrentMonthYear(null);
    setCurrentYear(null);
    setCurrentMonth(null);
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    setDownloadPassword(e.target.value);
  };

  // Handle password submit
  const handlePasswordSubmit = () => {
    if (!downloadPassword.trim()) {
      message.error('Please enter the download password');
      return;
    }
    
    setPasswordModalVisible(false);
    handleDownloadMonthDetails(currentYear, currentMonth, downloadPassword);
  };
  
  // Download campaign details for a specific month
  const handleDownloadMonthDetails = async (year, month, password) => {
    setDownloadLoading(true);
    try {
      const response = await getMailFilesTimelineMonthlyDetails(
        year,
        month,
        password,
        selectedClients,
        selectedCategory,
        selectedMailingLists
      );
      
      if (response && response.data && response.data.data) {
        const fileName = `Campaigns_${response.data.month_year.replace(' ', '_')}_${getFormattedDate()}`;
        
        const data = response.data.data.map(campaign => ({
          'Source Code': campaign.source_code,
          'Company': campaign.company,
          'Category': campaign.category,
          'Mailing List': campaign.mailing_list,
          'Mail Date': new Date(campaign.mail_date).toLocaleDateString(),
          'Customer Count': campaign.customer_count
        }));
        
        const columns = [
          { title: 'Source Code', dataIndex: 'Source Code' },
          { title: 'Company', dataIndex: 'Company' },
          { title: 'Category', dataIndex: 'Category' },
          { title: 'Mailing List', dataIndex: 'Mailing List' },
          { title: 'Mail Date', dataIndex: 'Mail Date' },
          { title: 'Customer Count', dataIndex: 'Customer Count' }
        ];
        
        exportToExcel(data, fileName, columns);
        message.success(`Downloaded campaign details for ${response.data.month_year}`);
      } else {
        message.error('Failed to download campaign details: Invalid response format');
      }
      
      setDownloadLoading(false);
    } catch (error) {
      console.error("Error downloading campaign details:", error);
      if (error.response && error.response.status === 403) {
        message.error('Incorrect password. Please try again.');
      } else {
        message.error(`Failed to download campaign details: ${error.message || 'Unknown error'}`);
      }
      setDownloadLoading(false);
    }
  };

  const columns = [
    {
      title: 'Month Year',
      dataIndex: 'month_year',
      key: 'month_year',
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => {
        // Sort by year first, then by month
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.month - b.month;
      },
    },
    {
      title: 'Customer Count',
      dataIndex: 'customer_count',
      key: 'customer_count',
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.customer_count - b.customer_count,
    },
    {
      title: 'Download',
      key: 'download',
      render: (_, record) => (
        <Tooltip title="Download campaign details for this month">
          <Button
            type="link"
            icon={<DownloadOutlined />}
            loading={downloadLoading && currentMonthYear === record.month_year}
            onClick={() => record.onDownload()}
          />
        </Tooltip>
      ),
    }
  ];

  return (
    <div className="mf-vs-timeline-tab">
      <Card className="filter-card mb-4">
        <div className="row align-items-center">
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">Category</label>
            <Select
              mode="multiple"
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={categoryOptions}
              className="w-100"
              placeholder="Select Category"
              maxTagCount="responsive"
            />
          </div>
          
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">List Count</label>
            <Select
              value={selectedListCount}
              onChange={handleListCountChange}
              className="w-100"
              placeholder="Select List Count"
              options={[
                { value: 'all', label: 'All List Counts' },
                { value: '1', label: '1 List' },
                { value: '2', label: '2 Lists' },
                { value: '3', label: '3 Lists' },
                { value: '4', label: '4 Lists' },
                { value: '5', label: '5 Lists' },
                { value: '6', label: '6+ Lists' }
              ]}
            />
          </div>
          
          <div className="col-md-4 mb-3 mb-md-0">
            <label className="form-label">Mailing Lists</label>
            <MailingListFilter 
              mailingListOptions={mailingListOptions}
              selectedMailingLists={selectedMailingLists}
              onMailingListsChange={handleMailingListsChange}
              loadingMailingLists={loadingMailingLists}
              onSelectAll={handleSelectAllMailingLists}
              onDeselectAll={handleDeselectAllMailingLists}
            />
          </div>
          
          <div className="col-md-4 d-flex align-items-end">
            <div className="d-flex gap-2 mt-md-4">
              <Button 
                type="primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
              <Button 
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Total Unique Customers: {totalUniqueCustomers}</h5>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleExportToExcel}
          disabled={reportData.length === 0}
        >
          Export to Excel
        </Button>
      </div>
      
      <Spin spinning={loading}>
        <Table 
          dataSource={reportData} 
          columns={columns}
          rowKey="key"
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Spin>
      
      {/* Password Modal */}
      <Modal
        title="Password Required"
        open={passwordModalVisible}
        onCancel={handlePasswordModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handlePasswordSubmit}>
            Download
          </Button>,
        ]}
      >
        <p>Please enter the password to download campaign details for {currentMonthYear}:</p>
        <Input.Password
          prefix={<LockOutlined />}
          value={downloadPassword}
          onChange={handlePasswordChange}
          placeholder="Enter password"
          onPressEnter={handlePasswordSubmit}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default MFvsTimelineMonthsTab;